"use client";

import { ChevronDown, Plus, Settings } from "lucide-react";
import type { Workspace } from "@/lib/api";
import { WorkspaceAvatarDisplay } from "@/app/components/WorkspaceAvatarDisplay";
import {
  WorkspaceSelectList,
  WORKSPACE_SELECT_ACTION_ROW_CLASS,
} from "@/app/components/WorkspaceSelectList";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import {
  DEFAULT_WORKSPACE_AVATAR,
  type WorkspaceAvatarSelection,
} from "@/lib/workspaceAvatar";

export type WorkspaceSwitcherVariant = "rail" | "top" | "mobile";

export function countWorkspacesByAccess(workspaces: Workspace[]): {
  owned: number;
  shared: number;
} {
  let owned = 0;
  let shared = 0;
  for (const workspace of workspaces) {
    if (workspace.access === "member") shared += 1;
    else owned += 1;
  }
  return { owned, shared };
}

/** Subtitle under the current shop name: owned count / shared-only copy. */
export function formatWorkspaceSwitcherSubtitle(workspaces: Workspace[]): string {
  const { owned, shared } = countWorkspacesByAccess(workspaces);
  if (owned <= 0 && shared > 0) return `แชร์จากผู้อื่น ${shared}`;
  const ownedLabel = `${owned} แหล่งข้อมูล`;
  if (shared <= 0) return ownedLabel;
  return `${ownedLabel} ของคุณ · แชร์จากผู้อื่น ${shared}`;
}

type WorkspaceSwitcherProps = {
  workspaces: Workspace[];
  selectedWorkspaceId?: string | null;
  workspaceLabel: string;
  workspaceAvatar?: WorkspaceAvatarSelection;
  subtitle?: string;
  isLoading?: boolean;
  ready?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (workspaceId: string) => void;
  onOpenSettings?: () => void;
  onAddWorkspace?: () => void;
  variant?: WorkspaceSwitcherVariant;
  avatarSize?: number;
  iconSize?: number;
};

export function WorkspaceSwitcher({
  workspaces,
  selectedWorkspaceId,
  workspaceLabel,
  workspaceAvatar = DEFAULT_WORKSPACE_AVATAR,
  subtitle,
  isLoading = false,
  ready = true,
  open,
  onOpenChange,
  onSelect,
  onOpenSettings,
  onAddWorkspace,
  variant = "rail",
  avatarSize,
  iconSize,
}: WorkspaceSwitcherProps) {
  const resolvedAvatarSize =
    avatarSize ?? (variant === "mobile" ? 36 : 32);
  const resolvedIconSize = iconSize ?? (variant === "mobile" ? 18 : 16);
  const resolvedSubtitle =
    subtitle ?? formatWorkspaceSwitcherSubtitle(workspaces);

  const triggerClass =
    variant === "rail"
      ? "flex h-full w-full min-w-0 cursor-pointer items-center gap-2 px-4 text-left"
      : variant === "top"
        ? "flex h-full min-w-0 shrink-0 cursor-pointer items-center gap-2.5 border-r border-aera-lightgray px-4 text-left"
        : "mx-4 mt-3 flex w-[calc(100%-2rem)] cursor-pointer items-center gap-3 rounded-2xl border border-aera-lightgray bg-aera-white px-3 py-2.5 text-left";

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="fw-workspace-switcher"
          aria-label={`เลือก Workspace: ${workspaceLabel}, ${resolvedSubtitle}`}
          aria-expanded={open}
          title={`${workspaceLabel} · ${resolvedSubtitle}`}
          className={triggerClass}
        >
          {ready ? (
            <>
              <WorkspaceAvatarDisplay
                avatar={workspaceAvatar}
                size={resolvedAvatarSize}
                iconSize={resolvedIconSize}
                className="shrink-0"
              />
              <div
                className={
                  variant === "top" ? "min-w-0" : "min-w-0 flex-1"
                }
              >
                <p className="truncate text-sm font-semibold text-aera-charcoal">
                  {workspaceLabel}
                </p>
                <p className="truncate text-xs text-aera-gray">
                  {resolvedSubtitle}
                </p>
              </div>
            </>
          ) : (
            <>
              <div
                className="shrink-0 animate-pulse rounded-lg bg-aera-lightgray"
                style={{
                  width: resolvedAvatarSize,
                  height: resolvedAvatarSize,
                }}
                aria-hidden
              />
              <div
                className={variant === "top" ? "min-w-0" : "min-w-0 flex-1"}
                aria-busy="true"
                aria-label="กำลังโหลดร้าน"
              >
                <div className="h-4 w-28 animate-pulse rounded bg-aera-lightgray" />
                <div className="mt-1.5 h-3 w-20 animate-pulse rounded bg-aera-lightgray/70" />
              </div>
            </>
          )}
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-aera-gray transition-transform duration-200 ease-out ${
              open ? "rotate-180" : "rotate-0"
            }`}
            aria-hidden
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        collisionPadding={variant === "mobile" ? 16 : 12}
        className={[
          "!z-[300] flex flex-col overflow-hidden rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)] animate-in fade-in zoom-in-95 duration-200 max-h-[min(var(--radix-dropdown-menu-content-available-height),calc(100dvh-5rem))]",
          variant === "mobile"
            ? "w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]"
            : "min-w-[240px] max-w-[min(280px,calc(100vw-2rem))]",
        ].join(" ")}
      >
        <p className="px-2.5 pb-0.5 pt-1 text-xs font-bold uppercase tracking-[0.05em] text-aera-gray">
          ร้าน / พื้นที่ทำงาน
        </p>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <WorkspaceSelectList
            asMenu
            workspaces={workspaces}
            selectedWorkspaceId={selectedWorkspaceId}
            isLoading={isLoading}
            onSelect={onSelect}
          />
        </div>
        {(onOpenSettings || onAddWorkspace) && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <div className="flex shrink-0 flex-col gap-0.5">
              {onOpenSettings ? (
                <DropdownMenuItem
                  className={WORKSPACE_SELECT_ACTION_ROW_CLASS}
                  onSelect={onOpenSettings}
                  disabled={!selectedWorkspaceId}
                >
                  <Settings size={16} strokeWidth={2.5} className="shrink-0" />
                  <span>ตั้งค่าร้านนี้</span>
                </DropdownMenuItem>
              ) : null}
              {onAddWorkspace ? (
                <DropdownMenuItem
                  className={WORKSPACE_SELECT_ACTION_ROW_CLASS}
                  onSelect={onAddWorkspace}
                >
                  <Plus size={16} strokeWidth={2.5} className="shrink-0" />
                  <span>เพิ่มร้าน / พื้นที่ใหม่</span>
                </DropdownMenuItem>
              ) : null}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
